import cn from 'classnames';
import { useContext, useMemo } from 'react';

import Text from 'components/common/Text';
import Logo from 'components/signUpFunnel/Logo';
import { BrandContext } from 'context/BrandContext';
import { getFunnelProgressSteps } from 'helpers/funnelProgress';
import useFunnelNavigation from 'hooks/useFunnelNavigation';
import styles from 'styles/components/signUpFunnel/ProgressBar';

import BackButton from './BackButton';
import { FunnelContext } from './Contexts';

const normalizePercent = value => {
  const parsedValue = parseInt(value);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const useCurrentProgress = () => {
  const { locationPercentage } = useFunnelNavigation();
  return normalizePercent(locationPercentage);
};

const StepIndicator = () => {
  const currentProgress = useCurrentProgress();
  const { funnel } = useContext(FunnelContext);
  const { brand } = useContext(BrandContext);

  const steps = useMemo(() => getFunnelProgressSteps(funnel), [funnel]);

  return (
    <div className={cn(styles.stepLabels, 'flex justify-between pb-2')}>
      {steps.map(step => {
        const stepInProgressOrCompleted = currentProgress >= step.locationPercentage;

        return (
          <Text
            key={step.name}
            brandStylesOverrides="h7"
            className={cn('hidden flex-none text-xs font-medium bp1:block', {
              [styles[brand]]: stepInProgressOrCompleted,
              'text-charcoal-90': !stepInProgressOrCompleted,
            })}
          >
            {step.name}
          </Text>
        );
      })}
    </div>
  );
};

const CompletedBar = () => {
  const currentProgress = useCurrentProgress();
  const { brand } = useContext(BrandContext);
  const mobileProgress = currentProgress;

  return (
    <div
      className={cn(styles.progressBarBackground, 'relative h-2 rounded bg-white-100 bp1:h-1.5')}
    >
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-0 block h-full rounded bp1:hidden',
          styles.progressBarForeground,
          styles[brand]
        )}
        style={{ width: `${mobileProgress}%` }}
      />
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-0 h-full rounded',
          styles.progressBarForeground,
          styles[brand]
        )}
        style={{ width: `${currentProgress}%` }}
      />
    </div>
  );
};

const MobileProgressBar = () => {
  return (
    <>
      <div className="relative flex items-center pt-2 bp1:hidden">
        <BackButton />
        <div className={cn('absolute left-1/2 -translate-x-1/2')}>
          <Logo />
        </div>
      </div>
      <div className={cn(styles.progressBarWrapper, 'mt-6 bp1:hidden')}>
        <CompletedBar />
      </div>
    </>
  );
};

const DesktopProgressBar = () => {
  return (
    <>
      <div className="hidden justify-between bp1:flex">
        <BackButton />
        <div className={cn(styles.progressBarWrapper, 'flex-initial')}>
          <StepIndicator />
          <CompletedBar />
        </div>
        <div className="flex-initial">
          <Logo />
        </div>
      </div>
    </>
  );
};

const ProgressBar = () => {
  const { brand } = useContext(BrandContext);

  return (
    <div className={cn(styles.header, styles[brand], 'sticky top-0 z-10 py-6 bp1:relative')}>
      <MobileProgressBar />
      <DesktopProgressBar />
    </div>
  );
};

export default ProgressBar;
